import React, { useState } from 'react';
import Icon from '../AppIcon';

/**
 * ShareButtons — Social sharing component
 * Supports: X, Facebook, Instagram, TikTok, WhatsApp, Email, Copy Link
 *
 * Usage:
 *   <ShareButtons
 *     title="Elon Musk — Who You Makin' Rich"
 *     text="Check out Elon Musk's financial connections on Who You Makin' Rich"
 *     url="https://whoyoumakinrich.com/individual-profile?id=xxx"
 *     compact={true}       // smaller buttons for cards
 *   />
 */
const ShareButtons = ({ title = '', text = '', url = '', compact = false }) => {
  const [copied, setCopied] = useState(false);

  const shareUrl = url || window.location.href;
  const shareTitle = title || document.title;
  const shareText = text || `Check this out on Who You Makin' Rich`;

  const encoded = {
    url: encodeURIComponent(shareUrl),
    title: encodeURIComponent(shareTitle),
    text: encodeURIComponent(shareText),
  };

  // Custom SVG icons for platforms Lucide doesn't cover
  const XLogo = ({ size = 16 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );

  const InstagramLogo = ({ size = 16 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );

  const TikTokLogo = ({ size = 16 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46V13.2a8.16 8.16 0 005.58 2.17V11.9a4.84 4.84 0 01-2.43-.66 4.83 4.83 0 01-1.57-1.6V6.69h4z" />
    </svg>
  );

  const shareLinks = [
    {
      name: 'X',
      customIcon: XLogo,
      href: `https://x.com/intent/post?text=${encoded.text}&url=${encoded.url}`,
      color: 'hover:bg-black hover:text-white',
      bg: 'bg-gray-100',
    },
    {
      name: 'Facebook',
      icon: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encoded.url}&quote=${encoded.text}`,
      color: 'hover:bg-[#1877F2] hover:text-white',
      bg: 'bg-blue-50',
    },
    {
      name: 'Instagram',
      customIcon: InstagramLogo,
      href: `https://www.instagram.com/`,
      color: 'hover:bg-gradient-to-br hover:from-[#f09433] hover:via-[#e6683c] hover:to-[#dc2743] hover:text-white',
      bg: 'bg-pink-50',
    },
    {
      name: 'TikTok',
      customIcon: TikTokLogo,
      href: `https://www.tiktok.com/`,
      color: 'hover:bg-black hover:text-white',
      bg: 'bg-gray-100',
    },
    {
      name: 'WhatsApp',
      icon: 'MessageCircle',
      href: `https://wa.me/?text=${encoded.text}%20${encoded.url}`,
      color: 'hover:bg-[#25D366] hover:text-white',
      bg: 'bg-green-50',
    },
    {
      name: 'Email',
      icon: 'Mail',
      href: `mailto:?subject=${encoded.title}&body=${encoded.text}%0A%0A${encoded.url}`,
      color: 'hover:bg-gray-700 hover:text-white',
      bg: 'bg-gray-100',
    },
  ];

  const handleCopyLink = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
      } catch {} // User cancelled
    }
  };

  const btnSize = compact ? 'w-7 h-7' : 'w-9 h-9';
  const iconSize = compact ? 13 : 16;

  return (
    <div
      className="flex items-center gap-1.5 flex-wrap"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Native share button (mobile) */}
      {typeof navigator !== 'undefined' && navigator.share && (
        <button
          onClick={handleNativeShare}
          className={`${btnSize} rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center transition-all hover:bg-indigo-600 hover:text-white hover:scale-110`}
          title="Share"
        >
          <Icon name="Share2" size={iconSize} />
        </button>
      )}

      {/* Social share links */}
      {shareLinks.map((link) => (
        <a
          key={link.name}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`${btnSize} rounded-full ${link.bg} text-gray-600 flex items-center justify-center transition-all ${link.color} hover:scale-110`}
          title={`Share on ${link.name}`}
          onClick={(e) => e.stopPropagation()}
        >
          {link.customIcon ? <link.customIcon size={iconSize} /> : <Icon name={link.icon} size={iconSize} />}
        </a>
      ))}

      {/* Copy link button */}
      <button
        onClick={handleCopyLink}
        className={`${btnSize} rounded-full ${copied ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'} flex items-center justify-center transition-all hover:bg-gray-700 hover:text-white hover:scale-110`}
        title={copied ? 'Copied!' : 'Copy link'}
      >
        <Icon name={copied ? 'Check' : 'Link'} size={iconSize} />
      </button>
    </div>
  );
};

export default ShareButtons;
